// One screen's operational card on the Overview: freshness badge, the value
// showing right now, and the quick offset — the three things an operator
// touches daily, with the full editor one click away.

import { Button, Card, toast } from "@pej/kore/components";
import { FormatRelativeTime } from "@pej/kore/intl";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { ApiError } from "@/api/error";
import { statusKeys } from "@/api/keys";
import { useOffsetDelta } from "@/api/tuning";
import type { StatusRow } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { ScreenFreshnessBadge } from "@/features/screens/screen-freshness";

const DELTAS = [-5, -1, 1, 5] as const;

export interface ScreenStatusCardProps {
    workspaceId: string;
    row: StatusRow;
}

export function ScreenStatusCard({ workspaceId, row }: ScreenStatusCardProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const offsetDelta = useOffsetDelta(row.screen.id);

    async function nudge(deltaMinutes: number) {
        try {
            const result = await offsetDelta.mutateAsync(deltaMinutes);
            toast.success(`Offset is now ${result.offsetMinutes > 0 ? "+" : ""}${result.offsetMinutes} min`);
            await queryClient.invalidateQueries({ queryKey: statusKeys.list(workspaceId) });
        } catch (err) {
            if (err instanceof ApiError) {
                toastMutationError("Offset change failed", err);
                return;
            }
            throw err;
        }
    }

    return (
        // Whole-card click-through, same affordance as data-table rows; the
        // inner controls stop propagation. Keyboard access rides on the name
        // link, so the card itself stays out of the tab order.
        <Card
            className="flex cursor-pointer flex-col gap-3 p-5 transition-colors hover:bg-kore-accent-softer"
            data-testid={`status-${row.screen.id}`}
            onClick={() =>
                void navigate({
                    to: "/w/$workspaceId/screens/$screenId",
                    params: { workspaceId, screenId: row.screen.id },
                })
            }
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <Link
                        to="/w/$workspaceId/screens/$screenId"
                        params={{ workspaceId, screenId: row.screen.id }}
                        className="truncate font-medium hover:underline"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {row.screen.name}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">{row.location.name}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Open live display"
                        render={
                            <a
                                href={`${window.location.origin}${row.screen.displayPath}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                            />
                        }
                    >
                        <ExternalLink aria-hidden="true" />
                    </Button>
                    <ScreenFreshnessBadge
                        active={row.screen.active}
                        serviceLastSeenAt={row.location.serviceLastSeenAt}
                        latestComputedAt={row.latest?.computedAt ?? null}
                    />
                </div>
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold tabular-nums">
                    {row.latest ? row.latest.displayedMinutes : "—"}
                </span>
                <span className="text-sm text-muted-foreground">min</span>
                {row.latest && (
                    <span className="ml-auto text-xs text-muted-foreground">
                        <FormatRelativeTime value={new Date(row.latest.computedAt)} /> · {row.latest.sampleCount} orders
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                    Offset <span className="font-medium text-foreground">{row.screen.offsetMinutes} min</span>
                </span>
                <div className="ml-auto flex gap-1">
                    {DELTAS.map((delta) => (
                        <Button
                            key={delta}
                            variant="outline"
                            size="sm"
                            disabled={offsetDelta.isPending}
                            onClick={(event) => {
                                event.stopPropagation();
                                void nudge(delta);
                            }}
                        >
                            {delta > 0 ? `+${delta}` : `${delta}`}
                        </Button>
                    ))}
                </div>
            </div>
        </Card>
    );
}
